(ns app.domain.application.condition.model
  (:require [app.domain.party.model :as party-model]
            [app.domain.user.authority :as authority]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [core.db.child-entity :as child]
            [core.db.entity.util :as eu]
            [core.util.schemas :as schemas]
            [potpuri.core :as p]
            [schema-tools.core :as st]
            [schema.core :as s]))


(s/defschema ConditionContent
  (st/merge {:application.condition/title schemas/NonBlankString
             :application.condition/body  schemas/NonBlankString}
            (st/optional-keys {:application.condition/addressed-to         s/Any
                               :application.condition/due-date             s/Inst
                               :application.condition/attachment-required? s/Bool})))


(def condition-required-keys
  [:application.condition/title
   :application.condition/body])


(def valid-condition? (comp nil? (s/checker ConditionContent)))


(def comment-states [:checklist.comment.state/draft
                     :checklist.comment.state/open
                     :checklist.comment.state/noted
                     :checklist.comment.state/responded
                     :checklist.comment.state/cleared])


(def applicant-editable-comment-states [:checklist.comment.state/open
                                        :checklist.comment.state/noted])


(def auth-only-comment-states
  #{:checklist.comment.state/draft})


(def condition-editable-keys
  (concat condition-required-keys
          [:application.condition/closed
           :application.condition/closed-by
           :application.condition/due-date
           :application.condition/attachment-required?
           :domain/state]))


(def condition-kinds
  #{:application.condition.kind/permit-specific-condition ; The default
    :application.condition.kind/general-condition})

(def condition-origins
  {:entity.type/pre-consultation :application.condition.origin/pre-consultation
   :entity.type/application      :application.condition.origin/application
   :entity.type/execution        :application.condition.origin/execution})

(defn pre-consultation-origin?
  [condition]
  (= :application.condition.origin/pre-consultation
     (:application.condition/origin condition)))

(defn general-condition?
  [{:application.condition/keys [kind]}]
  (= :application.condition.kind/general-condition kind))

(s/defschema ConditionTemplateContent
  (st/merge {:application.condition.template/title schemas/NonBlankString
             :application.condition.template/body  schemas/NonBlankString}
            (st/optional-keys {:application.condition.template/attachment-required? s/Bool})))

(def condition-template-editable-keys
  (keys (st/required-keys ConditionTemplateContent)))

(def valid-condition-template? (comp nil? (s/checker ConditionTemplateContent)))

(defn open? [condition]
  (and (nil? (-> condition :application.condition/closed))
       (du/in-state? :application.condition.state/published condition)))

(defn closed? [condition]
  (and (-> condition :application.condition/closed)
       ((du/in-state-fn :application.condition.state/published
                        :application.condition.state/overdue)
        condition)))

(defn draft? [condition]
  (du/in-state? :application.condition.state/draft condition))

(defn overdue? [condition]
  (and (nil? (-> condition :application.condition/closed))
       (du/in-state? :application.condition.state/overdue condition)))

(defn latest-approval-condition-with-result [condition]
  (->> condition
       :approval-condition/_condition
       (filter :approval-condition/result)
       (sort-by :approval-condition/result-ts)
       last))


(defn condition-in-open-approval?
  "Check if the condition is included in an approval that has not been finished yet,
  i.e. the condition cannot be included into another approval."
  [condition]
  (->> condition
       :approval-condition/_condition
       (map :entity/parent)
       (some #(-> % :approval/completed not))))


(defn condition-ineligible-for-approval?
  "A condition cannot be included in a circulation request if
  1. Condition is not in the same application/execution.
  2. Condition is still a draft.
  3. Condition has been closed (signed off by authority)
  4. Condition is included in an open circulation request."
  [process-target condition]
  (or (not= process-target (:entity/parent condition))
      (du/in-state? :application.condition.state/draft condition)
      (:application.condition/closed condition)
      (condition-in-open-approval? condition)))


(def approval-condition-result->condition-state
  {:approval.condition.result/not-cleared :condition/not-cleared
   :approval.condition.result/cleared     :condition/cleared})


(defn condition->state
  [condition]
  (let [{:approval-condition/keys [result]} (latest-approval-condition-with-result condition)
        published?                          (du/in-state? :application.condition.state/published condition)
        overdue?                            (du/in-state? :application.condition.state/overdue condition)]
    (cond
      (not (or published? overdue?)) :condition/draft
      (closed? condition)            :condition/closed
      overdue?                       :condition/overdue
      result                         (approval-condition-result->condition-state result)
      :else                          :condition/open)))

(def ^{:arglists '([phase-entity])} phase-conditions
  (child/child-entities-of-type-fn :entity.type/application-condition))

(defn conditions-of-the-ws [ws]
  (->> ws
       workspace-model/phase-entities
       (mapcat phase-conditions)))

(defn all-conditions-closed? [ws]
  (->> ws
       conditions-of-the-ws
       (remove general-condition?)
       (every? (fn [c]
                 (and (:application.condition/closed c)
                      (not (overdue? c)))))))

(def condition-snippet-variables
  [{:variable-name "condition-number"}
   {:variable-name "condition-name"}
   {:variable-name "condition-description"}
   {:variable-name "status"}
   {:variable-name "cleared-through-circulation"}
   {:variable-name "attachment-name"}
   {:variable-name "condition-attachment-names"}
   {:variable-name "due-date"}])

(def condition-snippet-table-variables
  [{:variable-name "condition-items" :snippet? true}])

(def ^{:arglist '([phase-entity])} phase-topics
  (child/child-entities-of-type-fn :entity.type/checklist-topic))

(defn topics-of-the-ws [ws]
  (->> ws
       workspace-model/phase-entities
       (mapcat phase-topics)))


(defn comments-of-topic [topic]
  (->> topic
       :entity/_parent
       (filter (eu/is-type-fn :entity.type/checklist-comment))
       (sort-by :entity/created)))


(defn comment-can-be-edited?
  "comment can be edited in status Draft or in Open if there are no responses"
  [state responses]
  (or (= state :checklist.comment.state/draft)
      (not (seq responses))))


(defn comment-state-can-be-changed?
  "only comment without responses can be changed back to Draft"
  [new-state responses]
  (and (contains? (set comment-states) new-state)
       (or (not= new-state :checklist.comment.state/draft)
           (not (seq responses)))))


(defn available-assignees-for-comment [phase auth-group-id]
  (->> phase
       party-model/phase-parties
       (filter #(p/if-all-let [user             (:party/user %)
                               user-auth-groups (-> user authority/authority-groups seq)]
                  (not (contains? (->> user-auth-groups (map :db/id) set) auth-group-id))
                  true))))


(defn comment-responses [comment]
  (->> comment
       :entity/_parent
       (filter (eu/is-type-fn :entity.type/checklist-response))))


(defn enabled-for-group?
  "Return `true`, if the conditions feature is enabled for the given group"
  [group]
  (-> group
      :group/features
      (contains? :group.feature/conditions)))

(defn template-snippets-enabled?
  [group feature-flags]
  (and (enabled-for-group? group)
       (contains? (or feature-flags #{})
                  :feature-flag.id/enable-condition-template-snippets)))


(def verdict-states-for-editing #{:verdict.state/draft})

(def ^{:arglists '([condition])} condition-files
  (child/child-entities-of-type-fn :entity.type/file))

(defn condition-attachments [condition]
  (->> condition
       condition-files
       (filter #(= :file.category/condition-attachment (:file/category %)))))

(defn attachment-required-but-missing? [condition]
  (and (:application.condition/attachment-required? condition)
       (empty? (condition-attachments condition))))
